import React, { useState, useEffect } from 'react';
import { geoAPI } from '../services/api';
import { ComparisonMode } from '../types/api';
import { colorPalette, fontFamily } from '../utils/careColors';
import {
  Search,
  LocationOn
} from '@mui/icons-material';

export type SortKey = 'default' | 'qol' | 'qoh' | 'los' | 'name';
export type SortDirection = 'asc' | 'desc';

export interface SortKeyConfig {
  value: SortKey;
  label: string;
}

interface SidebarControlsProps {
  onSearchResultClick?: (result: {
    community_name: string;
    latitude: number;
    longitude: number;
    corporation_name?: string;
  }) => void;
  availableCarelevels: string[];
  currentCareLevel: string;
  onCareLevelChange?: (careLevel: string) => void;
  sortKey: SortKey;
  onSortKeyChange: (key: SortKey) => void;
  sortDirection: SortDirection;
  onSortDirectionChange: (direction: SortDirection) => void;
  comparisonMode: ComparisonMode;
  onComparisonModeChange: (mode: ComparisonMode) => void;
}

const SidebarControls: React.FC<SidebarControlsProps> = ({
  onSearchResultClick,
  availableCarelevels,
  currentCareLevel,
  onCareLevelChange,
  sortKey,
  onSortKeyChange,
  sortDirection,
  onSortDirectionChange,
  comparisonMode,
  onComparisonModeChange
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Handle search with debouncing
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsSearching(true);
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const filters: { corporation_id?: string; community_id?: string } = {};
        const corpId = urlParams.get('corporation_id');
        const commId = urlParams.get('community_id');
        if (corpId) filters.corporation_id = corpId;
        if (commId) filters.community_id = commId;

        const results = await geoAPI.searchCommunities(searchQuery, 10, filters);
        // The backend returns 'locations' not 'results'
        if (results && Array.isArray(results.locations)) {
          setSearchResults(results.locations);
        } else {
          console.warn('Unexpected search results format:', results);
          setSearchResults([]);
        }
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const sortKeyOptions: SortKeyConfig[] = [
    { value: 'default', label: 'Default Order' },
    { value: 'qol', label: 'Quality of Life (QoL)' },
    { value: 'qoh', label: 'Quality of Health (QoH)' },
    { value: 'los', label: 'Length of Stay (LOS)' },
    { value: 'name', label: 'Community Name' },
  ];

  return (
    <>
      {/* Search Controls */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{
          fontSize: '14px',
          color: '#666',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          marginBottom: '4px'
        }}>
          <Search fontSize="small" /> Search Communities
        </label>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Type community name..."
          style={{
            width: '100%',
            padding: '8px 12px',
            fontSize: '14px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: 'white',
            fontFamily,
            color: '#333',
            boxSizing: 'border-box'
          }}
        />
        {isSearching && (
          <div style={{ marginTop: '4px', fontSize: '12px', color: '#666' }}>
            Searching...
          </div>
        )}
        {searchResults && searchResults.length > 0 && (
          <div style={{
            marginTop: '8px',
            maxHeight: '150px',
            overflowY: 'auto',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: 'white'
          }}>
            {searchResults.map((result, index) => (
              <div
                key={index}
                style={{
                  padding: '8px 12px',
                  borderBottom: index < searchResults.length - 1 ? '1px solid #eee' : 'none',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
                onClick={() => {
                  if (onSearchResultClick) {
                    onSearchResultClick(result);
                  }
                  // Clear search results after selection
                  setSearchQuery('');
                  setSearchResults([]);
                }}
              >
                <div style={{ fontWeight: 'bold' }}>{result.community_name}</div>
                {result.corporation_name && (
                  <div style={{ fontSize: '12px', color: '#666', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <LocationOn fontSize="inherit" /> {result.corporation_name}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Care Level Filter */}
      {availableCarelevels.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <label style={{
            fontSize: '14px',
            color: '#666',
            fontWeight: '500',
            display: 'block',
            marginBottom: '4px'
          }}>
            🏥 Care Level
          </label>
          <select
            value={currentCareLevel}
            onChange={(e) => onCareLevelChange && onCareLevelChange(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: '14px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: 'white',
              fontFamily,
              color: '#333',
              boxSizing: 'border-box'
            }}
          >
            <option value="">All Care Levels</option>
            {availableCarelevels.map((level) => {
              // Map care level codes to full names
              const getCareLevelName = (code: string) => {
                const mapping: Record<string, string> = {
                  'al': 'Assisted Living',
                  'il': 'Independent Living',
                  'mc': 'Memory Care',
                  'snf': 'Skilled Nursing',
                  'harmony_square': 'Harmony Square',
                  'vistas': 'Vistas'
                };
                return mapping[code.toLowerCase()] || code.split(' ').map(word =>
                  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                ).join(' ');
              };

              return (
                <option key={level} value={level}>
                  {getCareLevelName(level)}
                </option>
              );
            })}
          </select>
        </div>
      )}

      {/* Comparison Mode Toggle */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{
          fontSize: '14px',
          color: '#666',
          fontWeight: '500',
          display: 'block',
          marginBottom: '4px'
        }}>
          ⚖️ Compare against
        </label>
        <select
          value={comparisonMode}
          onChange={(e) => onComparisonModeChange(e.target.value as ComparisonMode)}
          style={{
            width: '100%',
            padding: '8px 12px',
            fontSize: '14px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: 'white',
            fontFamily,
            color: '#333',
            boxSizing: 'border-box',
            cursor: 'pointer'
          }}
        >
          <option value="radius">Nearby competitors (80 mi)</option>
          <option value="corporation">Own corporation</option>
        </select>
      </div>

      {/* Sort Controls */}
      <div style={{ marginTop: '12px' }}>
        <label style={{
          fontSize: '14px',
          color: '#666',
          fontWeight: '500',
          display: 'block',
          marginBottom: '4px'
        }}>
          Sort by:
        </label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <select
              value={sortKey}
              onChange={(e) => onSortKeyChange(e.target.value as SortKey)}
              style={{
                width: '100%',
                padding: '8px 10px',
                fontSize: '14px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                fontFamily,
                color: '#333',
                cursor: 'pointer'
              }}
            >
              {sortKeyOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {sortKey !== 'default' && (
            <div style={{ flex: 1, position: 'relative' }}>
              <select
                value={sortDirection}
                onChange={(e) => onSortDirectionChange(e.target.value as SortDirection)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  fontSize: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  fontFamily,
                  color: '#333',
                  cursor: 'pointer'
                }}
              >
                <option value="desc">
                  {sortKey === 'name' ? 'Z to A' : 'High to Low'}
                </option>
                <option value="asc">
                  {sortKey === 'name' ? 'A to Z' : 'Low to High'}
                </option>
              </select>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SidebarControls;
